"use client";
import axios from 'axios';
import React, { lazy, Suspense, useState, useEffect } from 'react';
import UsernameCursor from "./funcs/UsernameCursor";
// import HotjarScript from '@/app/funcs/tracking/hotjar';
import { ContactForm } from "./pages/contact/contactForm";
import Link from 'next/link';
import Image from 'next/image';
import { fetchPblicUsers } from './funcs/api';
import LazyComponent from './components/LazyComponent';
import Loader from './components/loader/loader';
import './lazy-loading.css';
import { EmblaCarousel } from './components/sliders/embala';
import ShortenComponent from './services/short/lnk/page';
import { getQuizzesList } from './services/quizzes/helpers/quizApi';
import {
  getChallengesForHome,
  getPostsForHome,
  getSessionsForHome,
  getJobsForHome,
  getFigplgsForHome,
  getFigtempsForHome,
  getSpeakersForHome,
  getShortAdsForHome,
} from './funcs/homeFeedApi';
// Import community components directly for immediate rendering
import CommunityHero from './components/CommunityHero';
import CommunityStats from './components/CommunityStats';
import ValueProposition from './components/ValueProposition';
import Testimonials from './components/Testimonials';
import StickyMobileCTA from './components/StickyMobileCTA';
import CalculatorPage from './services/calc/page';

// Lazy load heavy components
const LazyLatestChallengeParticipants = lazy(() => import('./components/LatestChallengeParticipants'));
// const LazyEmblaCarousel = lazy(() => import('./components/sliders/embala').then(module => ({ default: module.EmblaCarousel })));
const LazyEmblaCarousel = EmblaCarousel;
// const LazyDailyQuote = lazy(() => import('./components/dailyQuote/dailyQuote').then(module => ({ default: module.DailyQuote })));
// Loading components - using the new Loader component

const LoadingCard = () => (
  <div className="card h-100 border-0 shadow-sm">
    <div className="card-body text-center p-4">
      <div className="placeholder-glow">
        <div className="placeholder rounded-circle mx-auto mb-3" style={{ width: '60px', height: '60px' }}></div>
        <div className="placeholder col-8 mx-auto mb-2"></div>
        <div className="placeholder col-10 mx-auto mb-3"></div>
        <div className="placeholder col-4 mx-auto"></div>
      </div>
    </div>
  </div>
);

const Page = () => {

  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(true);

  // Home feed from REST (lazy: each section fetches when mounted via refs / one parallel fetch after mount)
  const [challengesData, setChallengesData] = useState(null);
  const [postsData, setPostsData] = useState(null);
  const [sessionsData, setSessionsData] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [figplgsData, setFigplgsData] = useState(null);
  const [figtempsData, setFigtempsData] = useState(null);
  const [speakersData, setSpeakersData] = useState(null);
  const [shortAdsData, setShortAdsData] = useState(null);

  // Lazy-load home feed sections: fetch all REST data in parallel after mount (non-blocking, like quizzes)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const settled = await Promise.allSettled([
          getChallengesForHome(),
          getPostsForHome(),
          getSessionsForHome(),
          getJobsForHome(),
          getFigplgsForHome(),
          getFigtempsForHome(),
          getSpeakersForHome(),
          getShortAdsForHome(),
        ]);
        if (cancelled) return;
        const pick = (i, fallback = []) => {
          const r = settled[i];
          if (r.status === 'fulfilled') return r.value;
          console.error('Home feed item failed:', r.reason);
          return fallback;
        };
        setChallengesData(pick(0, []));
        setPostsData(pick(1, []));
        setSessionsData(pick(2, []));
        setJobsData(pick(3, []));
        setFigplgsData(pick(4, []));
        setFigtempsData(pick(5, []));
        setSpeakersData(pick(6, []));
        setShortAdsData(pick(7, []));
      } catch (e) {
        if (!cancelled) console.error('Home feed REST error:', e);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Fetch quizzes for home section (same API and filter as services/quizzes: only non-ramadan, non-preview)
  useEffect(() => {
    let cancelled = false;
    const fetchQuizzes = async () => {
      try {
        setQuizzesLoading(true);
        const apiResponse = await getQuizzesList({ per_page: 50, page: 1 });
        if (cancelled) return;
        const apiQuizzes = apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
        const available = apiQuizzes.filter((q) => !q.ramadan && !q.preview);
        const mapped = available
          .slice(0, 6)
          .map((quiz) => ({
            id: quiz.id,
            title: quiz.title || 'بدون عنوان',
            description: quiz.excerpt || '',
            icon: quiz.icon || 'bi-grid-3x3-gap-fill',
            duration: quiz.q_duration ?? quiz.duration ?? 25,
            questions: quiz.totalQuestions ?? 0,
            participants: quiz.participants ?? 0,
          }));
        setQuizzes(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching quizzes for home:', err);
          setQuizzes([]);
        }
      } finally {
        if (!cancelled) setQuizzesLoading(false);
      }
    };
    fetchQuizzes();
    return () => { cancelled = true; };
  }, []);



  // Fetch admins for the avatars group
  const fetchAdmins = async () => {
    try {
      const allUsers = await fetchPblicUsers();



      // Filter admins and commadmins
      const allAdministrators = allUsers.filter(user =>
        user.roles && user.roles.includes('administrator')
      ).map(admin => ({ ...admin, role: 'administrator' }));

      const commadmins = allUsers.filter(user =>
        user.roles && user.roles.includes('commadmin') && !user.roles.includes('administrator')
      ).map(admin => ({ ...admin, role: 'commadmin' }));

      let arrangedAdmins = [];

      const maxDisplay = 7;

      if (allAdministrators.length > 0) {
        // If we have administrators, place them in the center
        const adminCount = Math.min(allAdministrators.length, 3); // Max 3 administrators in center
        const commadminCount = Math.min(commadmins.length, maxDisplay - adminCount);

        // Calculate how many commadmins to place before and after administrators
        const commadminsBefore = Math.floor(commadminCount / 2);
        const commadminsAfter = commadminCount - commadminsBefore;

        // Add commadmins before administrators
        arrangedAdmins.push(...commadmins.slice(0, commadminsBefore));

        // Add administrators in the center
        arrangedAdmins.push(...allAdministrators.slice(0, adminCount));

        // Add remaining commadmins after administrators
        arrangedAdmins.push(...commadmins.slice(commadminsBefore, commadminsBefore + commadminsAfter));
      } else {
        // If no administrators, just use commadmins
        arrangedAdmins = commadmins.slice(0, maxDisplay);
      }
      // }

      setAdmins(arrangedAdmins);
      setLoadingAdmins(false);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setLoadingAdmins(false);
    }
  };

  fetchAdmins();


  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };
    // Initial check
    checkMobile();

    // Add event listener for window resize
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkMobile);

      // Cleanup
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  return (
    <>


      {/* <HotjarScript /> */}
      {/* <UsernameCursor username="فيجماوية" /> */}


      {/* Video/Podcast Carousel */}
      <div className="mx-auto col-12 col-md-6 fade-in-up short-ads-carousel">
        <LazyEmblaCarousel sectionType='short_ad' data={shortAdsData} controls={true} img={{ size: 'medium_large', width: 900, height: 550, class: 'short_ad_cover' }} count={12} options={{  direction: 'rtl' }} section={{ bg: true }} />
      </div>


      <div className="px-3">
        {/* Community Hero Section */}
        <div id="community-hero">
          <CommunityHero />
        </div>



        {/* Community Stats Section */}
        {/* <CommunityStats graphQLData={graphQLData} /> */}
        {/* <CommunityStats graphQLData={graphQLData} /> */}

        {/* Value Proposition Section */}
        <ValueProposition />


        <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <LazyComponent threshold={0.1} rootMargin="100px">
            <Suspense fallback={<Loader />}>
              <LazyLatestChallengeParticipants data={challengesData} className='fade-in-up' />
            </Suspense>
          </LazyComponent>
        </div>

        {/* Testimonials Section */}
        {/* <Testimonials /> */}

        {/* Quizzes Section - data from quizzes API */}
        <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2">
              <i className="bi bi-clipboard-check"></i> الاختبارات
            </h2>
            <Link className='col-auto align-items-center d-flex' href={`/services/quizzes`}>
              عرض الكل <i className="bi bi-chevron-left"></i>
            </Link>
          </div>
          <div className="row g-4 mt-3">
            {quizzesLoading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="col-12 col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                    <div className="placeholder-glow p-4" style={{ minHeight: '150px' }} />
                    <div className="card-body p-4">
                      <span className="placeholder col-4 mb-2" />
                      <span className="placeholder col-8 d-block mb-2" />
                      <span className="placeholder col-12 mb-3" />
                      <span className="placeholder col-6" />
                    </div>
                  </div>
                </div>
              ))
            ) : quizzes.length > 0 ? (
              quizzes.map((quiz, index) => {
                const accentColors = ['#9C27B0', '#0ACF83', '#FF6B6B'];
                const color = accentColors[index % accentColors.length];
                const participantsStr = quiz.participants >= 1000 ? `${(quiz.participants / 1000).toFixed(1)}K` : String(quiz.participants);
                return (
                  <div key={quiz.id} className="col-12 col-md-6 col-lg-4">
                    <Link href={`/services/quizzes/${quiz.id}`} className="text-decoration-none">
                      <div
                        className="card border-0 shadow-sm h-100"
                        style={{
                          borderRadius: '16px',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = `0 8px 24px ${color}26`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center p-4"
                          style={{
                            background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                            minHeight: '150px'
                          }}
                        >
                          <i className={`bi ${quiz.icon}`} style={{ fontSize: '3rem', color }}></i>
                        </div>
                        <div className="card-body p-4">
                          <span className="badge bg-light text-dark border mb-2" style={{ fontSize: '0.7rem' }}>
                            اختبار
                          </span>
                          <h5 className="fw-bold mb-2">{quiz.title}</h5>
                          <p className="text-muted small mb-3">
                            {quiz.description}
                          </p>
                          <div className="d-flex align-items-center gap-3 text-muted small">
                            <span><i className="bi bi-clock"></i> {quiz.duration}د</span>
                            <span><i className="bi bi-file-earmark-text"></i> {quiz.questions} سؤال</span>
                            <span><i className="bi bi-people"></i> {participantsStr}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })
            ) : (
              <div className="col-12 text-center py-4 text-muted">
                لا يوجد اختبارات متاحة حالياً.
              </div>
            )}
          </div>
        </div>

        {/* 
      <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-8  fade-in-up">
        <h2 className="ms-2 ms-md-2">🤩 الأقسام التفاعلية</h2>
        <LazyComponent
          fallback={<Loader />}
          threshold={0.1}
          rootMargin="100px"
        >
          <Suspense fallback={<Loader />}>
            <LazyEmblaCarousel sectionType='community' data={graphQLData?.community?.nodes} controls={false} count={12} img={{ size: 'thumbnail', width: 80, height: 80, class: 'rounded-circle mb-3' }} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} id='community-sections' classes='embla-ships ms-0' section={{ title: true, content: false, bg: false }} />
          </Suspense>
        </LazyComponent>
      </div>

 */}


<div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2"> <i className='bi bi-pencil'></i> المقالات</h2>
            <Link className='col-auto align-items-center d-flex' href={`/sections/articles`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>
          <LazyComponent
            // fallback={<Loader />}
            threshold={0.1}
            rootMargin="100px"
          >
            {/* <Suspense fallback={<Loader />}> */}
            <LazyEmblaCarousel sectionType='articles' data={postsData} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
            {/* </Suspense> */}
          </LazyComponent>
        </div>
 



        <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-3 pb-0 ms-md-2"><i className='bi bi-play-btn flip-h'></i> الجلسات</h2> <Link className='col-auto align-items-center d-flex' href={`/sections/sessions`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>
          <LazyComponent
            fallback={<Loader />}
            threshold={0.1}
            rootMargin="100px"
          >
            <Suspense fallback={<Loader />}>
              <LazyEmblaCarousel sectionType='sessions' data={sessionsData} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
            </Suspense>
          </LazyComponent>
        </div>



        {/* <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2"> <i class="bi bi-briefcase"></i>  الوظائف</h2> <Link className='col-auto' href={`/sections/jobs`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>

          <LazyComponent
            threshold={0.1}
            rootMargin="100px"
          >
            <LazyEmblaCarousel sectionType='jobs' data={jobsData} controls={false} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
          </LazyComponent>
        </div> */}


        {/* <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2"><i class="bi bi-puzzle"></i> إضافات فيجما</h2>
            <Link className='col-auto align-items-center d-flex' href={`/sections/plugins`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>
          <LazyComponent
            threshold={0.1}
            rootMargin="100px"
          >
            <LazyEmblaCarousel isService={false} sectionType='plugins' data={figplgsData} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
          </LazyComponent>
        </div> */}


        {/* <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2"><i class="bi bi-file-earmark-text"></i> قوالب فيجما</h2>
            <Link className='col-auto align-items-center d-flex' href={`/sections/templates`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>
          <LazyComponent
            threshold={0.1}
            rootMargin="100px"
          >
            <LazyEmblaCarousel isService={false} sectionType='templates' data={figtempsData} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
          </LazyComponent>
        </div> */}


{/* 
        <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="row justify-content-between">
            <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2"><i className='bi bi-people'></i> مشاريع الأعضاء</h2>
            <Link className='col-auto align-items-center d-flex' href={`/services/design-reviews`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
          </div>
          <LazyComponent
            threshold={0.1}
            rootMargin="100px"
          >
            <LazyEmblaCarousel isService={true} sectionType='design-reviews' data={graphQLData?.designReviews?.nodes} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
          </LazyComponent>
        </div> */}



       


        {/* <div className="my-0 my-md-5 py-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
        <div className="row justify-content-between">
          <h2 className="col-auto ms-2 mb-0 pb-0 ms-md-2">🎙️  البودكاست  </h2>
          <Link className='col-auto' href={`/sections/podcasts`} > عرض الكل <i className="bi bi-chevron-left"></i> </Link>
        </div>
        <LazyComponent
          fallback={<Loader />}
          threshold={0.1}
          rootMargin="100px"
        >
          <Suspense fallback={<Loader />}>
            <LazyEmblaCarousel sectionType='podcasts' data={graphQLData?.podcasts?.nodes} controls={false} img={{ size: 'medium', width: 300, height: 300, class: 'content-section w-100' }} count={6} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} classes='embla-square ms-0' section={{ title: true, content: false, bg: true }} />
          </Suspense>
        </LazyComponent>
      </div> */}

        {/* Latest Challenge Participants Section */}
        {/* <LazyComponent
        fallback={<Loader />}
        threshold={0.1}
        rootMargin="100px"
      > */}
        {/* <Suspense fallback={<Loader />}> */}

        {/* </Suspense> */}

        {/* </LazyComponent> */}


        {/* <ShortenComponent /> */}


        {/* </div> */}





        {/* {slides && ( */}
        <div className="my-0 my-md-5 pb-5 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <h2 className="ms-2 ms-md-2">🌷 شكراً لكل الخبراء والضيوف</h2>
          <LazyComponent
            fallback={<Loader />}
            threshold={0.1}
            rootMargin="100px"
          >
            <Suspense fallback={<Loader />}>
              <LazyEmblaCarousel sectionType='speaker' link='sessions' data={speakersData} controls={false} img={{ size: 'thumbnail', width: 80, height: 80, class: 'rounded-circle mb-3' }} count={12} options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }} id='community-sections' classes='embla-ships ms-0' section={{ title: true, content: false, bg: false }} />
            </Suspense>
          </LazyComponent>
        </div>
        {/* )} */}

      </div>

      {/* <LazyComponent
        fallback={<Loader />}
        threshold={0.1}
        rootMargin="100px"
      >
        <Suspense fallback={<Loader />}>
          <LazyDailyQuote data={graphQLData?.quotes?.nodes} />
        </Suspense>
      </LazyComponent> */}


      {/* <LazyComponent
        fallback={<Loader />}
        threshold={0.1}
        rootMargin="100px"
      >
        <div className="py-4 mx-auto col-12  fade-in-up">
          <h2 className="mt-5 mx-auto  text-center"> عندك ملاحظة أو اقتراح ؟  </h2>
          <Suspense fallback={<Loader />}>
            <ContactForm />
          </Suspense>
        </div>
      </LazyComponent> */}

      {/* Admins Avatars Group Section */}
      <LazyComponent
        // fallback={<Loader />}
        threshold={0.1}
        rootMargin="100px"
      >
        <div className="my-0 my-md-5 py-4 mx-auto col-12 col-md-12 col-xxl-12  fade-in-up">
          <div className="text-center mb-4">
            <h2 className="fw-bold">👥 فريق مجتمع فيجماوية</h2>
            <p className="text-muted">تعرف على القادة ومديري مجتمع فيجماوية</p>
          </div>

          {loadingAdmins ? (
            <Loader
              size="medium"
              color="primary"
              text="جاري تحميل بيانات الفريق..."
              showText={true}
            />
          ) : admins.length > 0 ? (
            <>
              <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 gap-md-3 mb-4">
                {admins.map((admin, index) => (
                  <Link
                    key={admin.id}
                    href='#'
                    // href={`/@${admin.slug}`}
                    className="text-decoration-none"
                    title={admin.name}
                  >
                    <div className="position-relative">
                      {(() => {
                        const avatarUrl = admin?.avatar?.url;
                        return avatarUrl;
                      })() ? (
                        <Image
                          src={admin.avatar.url}
                          alt={admin.name}
                          width={admin.role === 'administrator' ? 70 : 60}
                          height={admin.role === 'administrator' ? 70 : 60}
                          className={`rounded-circle border-3 border-white shadow-sm ${admin.role === 'administrator' ? 'border-danger' : ''}`}
                          style={{
                            width: admin.role === 'administrator' ? 'clamp(50px, 12vw, 70px)' : 'clamp(45px, 10vw, 60px)',
                            height: admin.role === 'administrator' ? 'clamp(50px, 12vw, 70px)' : 'clamp(45px, 10vw, 60px)',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            borderWidth: admin.role === 'administrator' ? 'clamp(3px, 0.8vw, 4px)' : 'clamp(2px, 0.6vw, 3px)',
                            borderColor: admin.role === 'administrator' ? '#dc3545' : 'white',
                            minWidth: '45px',
                            minHeight: '45px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = admin.role === 'administrator'
                              ? '0 6px 20px rgba(220, 53, 69, 0.3)'
                              : '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = admin.role === 'administrator'
                              ? '0 4px 15px rgba(220, 53, 69, 0.2)'
                              : '0 2px 8px rgba(0,0,0,0.1)';
                          }}
                        />
                      ) : (
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center border-3 border-white shadow-sm ${admin.role === 'administrator' ? 'bg-danger' : 'bg-secondary'}`}
                          style={{
                            width: admin.role === 'administrator' ? 'clamp(50px, 12vw, 70px)' : 'clamp(45px, 10vw, 60px)',
                            height: admin.role === 'administrator' ? 'clamp(50px, 12vw, 70px)' : 'clamp(45px, 10vw, 60px)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            borderWidth: admin.role === 'administrator' ? 'clamp(3px, 0.8vw, 4px)' : 'clamp(2px, 0.6vw, 3px)',
                            borderColor: admin.role === 'administrator' ? '#dc3545' : 'white',
                            minWidth: '45px',
                            minHeight: '45px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = admin.role === 'administrator'
                              ? '0 6px 20px rgba(220, 53, 69, 0.3)'
                              : '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = admin.role === 'administrator'
                              ? '0 4px 15px rgba(220, 53, 69, 0.2)'
                              : '0 2px 8px rgba(0,0,0,0.1)';
                          }}
                        >
                          <i className="bi bi-person-fill text-white" style={{ fontSize: 'clamp(1rem, 3vw, 1.5rem)' }}></i>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>

              {/* <div className="text-center">
                <Link
                  href="/admins"
                  className="btn btn-outline-primary btn-lg px-3 px-md-4 py-2 rounded-pill shadow-sm"
                  style={{
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #FFD52E 0%, #FF8C00 100%)',
                    border: 'none',
                    color: 'white',
                    fontSize: 'clamp(0.875rem, 3vw, 1.125rem)',
                    minHeight: '44px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(255, 213, 46, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 213, 46, 0.2)';
                  }}
                >
                  <i className="bi bi-people-fill" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}></i>
                  <span className="text-light">تعرف على كامل فريق مجتمع فيجماوية</span>
                  <i className="bi bi-chevron-left" style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}></i>
                </Link>
              </div> */}
            </>
          ) : (
            <div className="text-center">
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                لا يوجد أعضاء فريق متاحين حالياً
              </div>
            </div>
          )}
        </div>
      </LazyComponent>

      {/* Sticky Mobile CTA */}
      <StickyMobileCTA />
    </>
  );
};
export default Page;